/**
 * Extract a human-readable message from an axios-like error or an Error.
 *
 * FastAPI 422 responses have `detail` as an array of `{loc, msg, type, ...}`
 * objects. Default toast handlers stringify that to `[object Object]` —
 * useless to the operator. This collapses such arrays into «field — msg; …»
 * so the toast reads like "username — String should match pattern …".
 */
type DetailItem = { loc?: unknown[]; msg?: string; message?: string };

interface ErrorLike {
  response?: { data?: { detail?: string | DetailItem[] } };
  message?: string;
}

/** Подставить в ошибку причину отказа, пришедшую БЛОБОМ.
 *
 *  ⚠️ У запросов с `responseType: "blob"` тело ошибки — тоже Blob, поэтому `detail` из него не
 *  виден, и тост показывал «Request failed with status code 422» вместо настоящей причины
 *  («Состав не сходится — отправлять нельзя: …»). Дочитываем тело и кладём на место, чтобы дальше
 *  работал обычный `extractErrorMessage`.
 */
export async function readBlobError(err: unknown): Promise<unknown> {
  const e = err as { response?: { data?: unknown } };
  const body = e?.response?.data;
  if (!(body instanceof Blob) || !e.response) return err;
  const text = await body.text();
  try {
    e.response.data = JSON.parse(text);
  } catch {
    // не JSON — показываем тело как есть: это лучше, чем номер статуса вместо причины
    e.response.data = { detail: text.slice(0, 500) };
  }
  return err;
}

export function extractErrorMessage(err: unknown, fallback = "Ошибка"): string {
  const e = err as ErrorLike;
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d.loc)
          ? d.loc.filter((x) => x !== "body").join(".")
          : "";
        const msg = d.msg ?? d.message ?? "";
        return field ? `${field} — ${msg}` : msg;
      })
      .filter(Boolean)
      .join("; ") || fallback;
  }
  return e?.message ?? fallback;
}
